import React, { useState, useEffect, useMemo } from "react";
import { Col, Modal, Row } from "react-bootstrap";
import DataTable from "react-data-table-component";
import toast from "react-hot-toast";
import ItemService from "../../../../services/customer_appCommonService";
import { useSelector } from "react-redux";

// Roles that can mark credit note — adjust "PCD" to match your actual backend roleId
const CREDIT_NOTE_ROLES = ["SALES-ADMIN", "SALES-PCD"];

const ERFCreditNoteSection = ({
  documentNumber,
  isExtension = false,
  isRecordExtended = false,
}) => {
  const userData = useSelector((state) => state?.user);
  const currentUserId = userData?.data?.userId || "";

  const isSalesAdminOrPCD = useMemo(
    () =>
      userData?.data?.roles?.some((r) =>
        CREDIT_NOTE_ROLES.includes(r.roleId),
      ) || false,
    [userData],
  );

  const isStoreUser = useMemo(
    () => userData?.data?.roles?.some((r) => r.roleId === "STORE") || false,
    [userData],
  );

  const [erfRecord, setErfRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSectionVisible, setIsSectionVisible] = useState(true);

  const isInvoiceCreated = !!erfRecord?.invoiceCreated;
  const isFinanceConfirmed =
    erfRecord?.financeCollectionConfirmation === "Confirmed";
  const isLockedForCreditNote =
    isRecordExtended || isInvoiceCreated || isFinanceConfirmed;

  const [confirmModal, setConfirmModal] = useState({
    show: false,
    action: null, // "creditNote" | "stockIn" | "reverseCreditNote"
    row: null,
  });

  useEffect(() => {
    if (documentNumber) fetchERFData();
    // eslint-disable-next-line
  }, [documentNumber]);

  const fetchERFData = async () => {
    setLoading(true);
    try {
      const response = await ItemService.generateQuery({
        queryName: isExtension ? "ERFExtDetail" : "ERFDetail",
        documentNumber,
        timeZone: "Asia/Calcutta",
      });
      if (
        response?.status === 200 &&
        Array.isArray(response.data) &&
        response.data.length > 0
      ) {
        setErfRecord(response.data[0]);
      }
    } catch {
      toast.error("Failed to load ERF data for credit note section");
    } finally {
      setLoading(false);
    }
  };

  // Merge all item arrays, tagging each row with its source array
  const allItems = useMemo(() => {
    if (!erfRecord) return [];
    const items = [];
    (erfRecord.rentItems || []).forEach((item, i) =>
      items.push({ ...item, _sourceType: "rentItems", _localKey: `rent-${i}` }),
    );
    (erfRecord.saleItems || []).forEach((item, i) =>
      items.push({ ...item, _sourceType: "saleItems", _localKey: `sale-${i}` }),
    );
    (erfRecord.nursingItems || []).forEach((item, i) =>
      items.push({
        ...item,
        _sourceType: "nursingItems",
        _localKey: `nursing-${i}`,
      }),
    );
    (erfRecord.serviceItems || []).forEach((item, i) =>
      items.push({
        ...item,
        _sourceType: "serviceItems",
        _localKey: `service-${i}`,
      }),
    );
    // Accessories shown for tracking only — amount NOT counted in total
    (erfRecord.accessoryItems || []).forEach((item, i) =>
      items.push({
        ...item,
        _sourceType: "accessoryItems",
        _localKey: `acc-${i}`,
      }),
    );
    return items;
  }, [erfRecord]);

  const totalCreditNoteAmount = useMemo(
    () =>
      allItems.reduce((sum, item) => {
        // Accessories are tracked but their amount does NOT count in the total
        if (item._sourceType === "accessoryItems") return sum;
        if (
          item.creditNoteStatus === "CreditNote" ||
          item.creditNoteStatus === "StockIn"
        ) {
          return sum + (parseFloat(item.creditNoteAmount) || 0);
        }
        return sum;
      }, 0),
    [allItems],
  );

  // const buildPayload = (updatedItem, sourceType, extraFields = {}) => {
  //   const updatedArray = (erfRecord[sourceType] || []).map((existingItem) =>
  //     existingItem._id === updatedItem._id ? updatedItem : existingItem,
  //   );

  //   const allArrays = {
  //     rentItems: erfRecord.rentItems || [],
  //     saleItems: erfRecord.saleItems || [],
  //     nursingItems: erfRecord.nursingItems || [],
  //     serviceItems: erfRecord.serviceItems || [],
  //     accessoryItems: erfRecord.accessoryItems || [],
  //     [sourceType]: updatedArray,
  //   };

  //   // Accessories are NOT included in the credit note total sum
  //   const newTotal = [
  //     ...allArrays.rentItems,
  //     ...allArrays.saleItems,
  //     ...allArrays.nursingItems,
  //     ...allArrays.serviceItems,
  //   ].reduce((sum, item) => {
  //     if (
  //       item.creditNoteStatus === "CreditNote" ||
  //       item.creditNoteStatus === "StockIn"
  //     ) {
  //       return sum + (parseFloat(item.creditNoteAmount) || 0);
  //     }
  //     return sum;
  //   }, 0);

  //   return {
  //     documentNumber: erfRecord.documentNumber,
  //     equipmentRentNr: erfRecord.documentNumber,
  //     ...allArrays,
  //     totalCreditNoteAmount: parseFloat(newTotal.toFixed(2)),
  //     ...extraFields,
  //   };
  // };

  const SOURCE_KEY_PREFIX = {
    rentItems: "rent",
    saleItems: "sale",
    nursingItems: "nursing",
    serviceItems: "service",
    accessoryItems: "acc",
  };

  const buildPayload = (updatedItem, sourceType, extraFields = {}) => {
    const sourceArray = erfRecord[sourceType] || [];
    const expectedPrefix = SOURCE_KEY_PREFIX[sourceType];
    const [prefix, idxStr] = (updatedItem._localKey || "").split("-");

    if (prefix !== expectedPrefix) {
      throw new Error(
        `Source mismatch: localKey ${updatedItem._localKey} does not belong to ${sourceType}`,
      );
    }

    const targetIndex = parseInt(idxStr, 10);
    if (
      Number.isNaN(targetIndex) ||
      targetIndex < 0 ||
      targetIndex >= sourceArray.length
    ) {
      throw new Error(`Invalid index ${targetIndex} for ${sourceType}`);
    }

    // Strip internal fields BEFORE saving
    const { _localKey, _sourceType, ...cleanUpdatedItem } = updatedItem;

    const updatedArray = sourceArray.map((existingItem, idx) =>
      idx === targetIndex ? cleanUpdatedItem : existingItem,
    );

    const allArrays = {
      rentItems: erfRecord.rentItems || [],
      saleItems: erfRecord.saleItems || [],
      nursingItems: erfRecord.nursingItems || [],
      serviceItems: erfRecord.serviceItems || [],
      accessoryItems: erfRecord.accessoryItems || [],
      [sourceType]: updatedArray,
    };

    // const newTotal = [
    //   ...allArrays.rentItems,
    //   ...allArrays.saleItems,
    //   ...allArrays.nursingItems,
    //   ...allArrays.serviceItems,
    // ].reduce((sum, item) => {
    //   if (
    //     item.creditNoteStatus === "CreditNote" ||
    //     item.creditNoteStatus === "StockIn"
    //   ) {
    //     return sum + (parseFloat(item.creditNoteAmount) || 0);
    //   }
    //   return sum;
    // }, 0);

    // return {
    //   documentNumber: erfRecord.documentNumber,
    //   equipmentRentNr: erfRecord.documentNumber,
    //   ...allArrays,
    //   totalCreditNoteAmount: parseFloat(newTotal.toFixed(2)),
    //   ...extraFields,
    // };

    const billableItemsAfterUpdate = [
      ...allArrays.rentItems,
      ...allArrays.saleItems,
      ...allArrays.nursingItems,
      ...allArrays.serviceItems,
    ];

    const newTotal = billableItemsAfterUpdate.reduce((sum, item) => {
      if (
        item.creditNoteStatus === "CreditNote" ||
        item.creditNoteStatus === "StockIn"
      ) {
        return sum + (parseFloat(item.creditNoteAmount) || 0);
      }
      return sum;
    }, 0);

    // // Indicator stays true if ANY item is still credited after this update.
    // // On reverse, this flips to false only when the last credited item is cleared.
    // const anyStillCredited = billableItemsAfterUpdate.some(
    //   (item) =>
    //     item.creditNoteStatus === "CreditNote" ||
    //     item.creditNoteStatus === "StockIn",
    // );

    // return {
    //   documentNumber: erfRecord.documentNumber,
    //   equipmentRentNr: erfRecord.documentNumber,
    //   ...allArrays,
    //   totalCreditNoteAmount: parseFloat(newTotal.toFixed(2)),
    //   creditNoteIndicator: anyStillCredited,
    //   ...extraFields,
    // };

    // Indicator stays true if ANY item is still credited after this update.
    const anyStillCredited = billableItemsAfterUpdate.some(
      (item) =>
        item.creditNoteStatus === "CreditNote" ||
        item.creditNoteStatus === "StockIn",
    );

    // ── Extension status logic ─────────────────────────────────────────────
    // An ERF is EITHER a rent ERF (only rentItems) OR a nursing ERF (only
    // nursingItems) — never both. It is blocked from extension when EVERY
    // extendable item in it is credit-noted (StockIn also counts as settled).
    const isSettled = (item) =>
      item.creditNoteStatus === "CreditNote" ||
      item.creditNoteStatus === "StockIn";

    const rentArr = allArrays.rentItems;
    const nursingArr = allArrays.nursingItems;
    const hasRent = rentArr.length > 0;

    // Whichever category this ERF actually contains.
    const extensionItems = hasRent ? rentArr : nursingArr;
    const hasExtensionItems = extensionItems.length > 0;
    const shouldBlockExtension =
      hasExtensionItems && extensionItems.every(isSettled);

    const blockCode = hasRent ? "RENT_CN" : "NURSING_CN";
    const blockLabel = hasRent ? "Rent Credit Noted" : "Nursing Credit Noted";

    // erfRecord is freshly reloaded after every action; detect prior blocked
    // state by the status code so we know whether to snapshot or restore.
    const BLOCK_CODES = ["RENT_CN", "NURSING_CN"];
    const wasBlocked = BLOCK_CODES.includes(erfRecord.erfStatusCode);

    let extensionStatusFields = {};
    if (shouldBlockExtension && !wasBlocked) {
      // active -> blocked: snapshot the real status ONCE, then switch.
      extensionStatusFields = {
        preBlockErfStatus: erfRecord.erfStatus ?? null,
        preBlockErfStatusCode: erfRecord.erfStatusCode ?? null,
        erfStatus: blockLabel,
        erfStatusCode: blockCode,
      };
    } else if (!shouldBlockExtension && wasBlocked) {
      // blocked -> active (reverse): restore the snapshot.
      extensionStatusFields = {
        erfStatus: erfRecord.preBlockErfStatus ?? erfRecord.erfStatus,
        erfStatusCode:
          erfRecord.preBlockErfStatusCode ?? erfRecord.erfStatusCode,
        preBlockErfStatus: null,
        preBlockErfStatusCode: null,
      };
    }
    // else: no transition → leave erfStatus/erfStatusCode untouched.

    return {
      documentNumber: erfRecord.documentNumber,
      equipmentRentNr: erfRecord.documentNumber,
      ...allArrays,
      totalCreditNoteAmount: parseFloat(newTotal.toFixed(2)),
      creditNoteIndicator: anyStillCredited,
      ...extensionStatusFields,
      ...extraFields,
    };
  };

  const fetchMaterialRecord = async (barcode) => {
    try {
      const res = await ItemService.generateQuery({
        queryName: "MaterilBarcodeList",
        rowFilter: { $and: [{ barcode }] },
        timeZone: "Asia/Calcutta",
      });
      return res?.status === 200 && res.data?.length > 0 ? res.data[0] : null;
    } catch (err) {
      console.error("Fetch material barcode error", err);
      return null;
    }
  };

  const updateMaterialBarcodeForCreditNote = async (item) => {
    if (!item.barcode) return;

    const materialRecord = await fetchMaterialRecord(item.barcode);
    if (!materialRecord) return; // barcode record not found — don't block the flow

    const newHistoryEntry = {
      status: "CreditNote",
      equipmentRentNr: erfRecord?.documentNumber || "",
      equipmentItemNr: item.equipmentItemNr || "",
      erfTypeCode: item.erfTypeCode || "RE",
      updatedAt: new Date().toISOString(),
      updatedBy: currentUserId,
      updatedByName: currentUserId,
    };

    const updatedBarcodeHistory = [
      ...(materialRecord.barcodeERFHistory || []),
      newHistoryEntry,
    ];

    const payload = {
      barcode: item.barcode,
      documentNumber: materialRecord.documentNumber,
      materialStatus: "StockIn",
      assignBarcode: false,
      barCode: "StockIn",
      barcodeERFHistory: updatedBarcodeHistory,
    };

    const response = await ItemService.updateMaterialBarcodeItem(payload);

    if (response?.status !== 200) {
      throw new Error("Barcode status update failed");
    }
  };

  const executeAction = async () => {
    // const { action, row } = confirmModal;
    // setConfirmModal({ show: false, action: null, row: null });

    // const sourceType = row._sourceType;

    const { action, row } = confirmModal;

    if (!row || !action) {
      setConfirmModal({ show: false, action: null, row: null });
      return;
    }

    const sourceType = row._sourceType;
    setConfirmModal({ show: false, action: null, row: null });

    let updatedItem;
    if (action === "creditNote") {
      updatedItem = {
        ...row, // keep _localKey and _sourceType — buildPayload strips them
        creditNoteStatus: "CreditNote",
        creditNoteAmount: parseFloat(row.grossValue) || 0,
        creditNoteDate: new Date().toISOString(),
        creditNoteBy: currentUserId,
      };
    } else if (action === "stockIn") {
      updatedItem = {
        ...row,
        creditNoteStatus: "StockIn",
        stockInDate: new Date().toISOString(),
        stockInBy: currentUserId,
        productReturned: true,
        extensionStatus: "Closed",
      };
    } else if (action === "reverseCreditNote") {
      // while removing the
      // eslint-disable-next-line no-unused-vars
      const {
        creditNoteStatus,
        creditNoteAmount,
        creditNoteDate,
        creditNoteBy,
        ...rest
      } = row;
      updatedItem = rest;
    }

    // try {
    //   setLoading(true);

    //   const payload = buildPayload(
    //     updatedItem,
    //     sourceType,
    //     action === "creditNote" ? { creditNoteIndicator: true } : {},
    //   );
    //   await (isExtension
    //     ? ItemService.updateExtendEquipmentRent(payload)
    //     : ItemService.updateEquipmentRentForm(payload));

    //   if (action === "stockIn") {
    //     const barcodeTypes = ["rentItems", "saleItems", "accessoryItems"];
    //     if (barcodeTypes.includes(sourceType) && row.barcode) {
    //       try {
    //         await updateMaterialBarcodeForCreditNote(row);
    //       } catch {
    //         toast.error(
    //           "ERF updated but barcode history update failed. Please check barcode record.",
    //         );
    //       }
    //     }
    //   }

    //   toast.success(
    //     action === "creditNote"
    //       ? "Item marked as Credit Note"
    //       : "Stock In completed for item",
    //   );
    //   await fetchERFData();
    // } catch {
    //   toast.error(
    //     action === "creditNote"
    //       ? "Failed to mark Credit Note"
    //       : "Failed to mark Stock In",
    //   );
    // } finally {
    //   setLoading(false);
    // }
    try {
      setLoading(true);

      const payload = buildPayload(updatedItem, sourceType);
      await (isExtension
        ? ItemService.updateExtendEquipmentRent(payload)
        : ItemService.updateEquipmentRentForm(payload));

      if (action === "stockIn") {
        const barcodeTypes = ["rentItems", "saleItems", "accessoryItems"];
        if (barcodeTypes.includes(sourceType) && row.barcode) {
          try {
            await updateMaterialBarcodeForCreditNote(row);
          } catch {
            toast.error(
              "ERF updated but barcode history update failed. Please check barcode record.",
            );
          }
        }
      }

      const successMsg = {
        creditNote: "Item marked as Credit Note",
        stockIn: "Stock In completed for item",
        reverseCreditNote: "Credit note reversed",
      }[action];
      toast.success(successMsg);

      await fetchERFData();
    } catch {
      const errorMsg = {
        creditNote: "Failed to mark Credit Note",
        stockIn: "Failed to mark Stock In",
        reverseCreditNote: "Failed to reverse Credit Note",
      }[action];
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const openConfirm = (action, row) => {
    setConfirmModal({ show: true, action, row });
  };

  const renderStatusBadge = (status) => {
    const config = {
      CreditNote: { bg: "#dc3545", label: "Credit Note" },
      StockIn: { bg: "#28a745", label: "Stock In Done" },
    };
    const c = config[status];
    if (!c) return <span className="text-muted">--</span>;
    return (
      <span
        className="badge"
        style={{
          backgroundColor: c.bg,
          color: "white",
          padding: "5px 10px",
          borderRadius: "8px",
          fontSize: "11px",
        }}
      >
        {c.label}
      </span>
    );
  };

  const ITEM_TYPE_LABELS = {
    rentItems: "Rent",
    saleItems: "Sale",
    nursingItems: "Nursing",
    serviceItems: "Service",
  };

  const columns = [
    {
      name: "Products Code",
      cell: (row) => (
        <div>
          <p className="fw-bold m-0">{row.materialCode}</p>
        </div>
      ),
      width: "135px",
    },
    {
      name: "Products Details",
      cell: (row) => (
        <div>
          <p className="fw-bold m-0">{row.materialDescription}</p>
        </div>
      ),
      width: "230px",
    },
    {
      name: "Barcode",
      cell: (row) => (
        <div>
          <p className="m-0 text-muted">{row.barcode}</p>
        </div>
      ),
      width: "200px",
    },
    {
      name: "Type",
      cell: (row) => (
        <span className="text-muted">
          {ITEM_TYPE_LABELS[row._sourceType] || "--"}
        </span>
      ),
      width: "90px",
    },
    {
      name: "Qty",
      cell: (row) => (
        <div>
          <p className="m-0 fw-bold">{row.orderedQuantity}</p>
          <p className="m-0 text-muted">{row.orderUnitOfMeasure}</p>
        </div>
      ),
      width: "80px",
    },
    {
      name: "Gross Value",
      cell: (row) => (
        <span className="fw-bold">
          {parseFloat(row.grossValue || 0).toFixed(2)}
        </span>
      ),
      width: "120px",
    },
    {
      name: "CN Amount",
      cell: (row) => {
        if (row._sourceType === "accessoryItems") {
          return <span className="text-muted">Not counted</span>;
        }
        return row.creditNoteAmount ? (
          <span className="fw-bold">
            {parseFloat(row.creditNoteAmount).toFixed(2)}
          </span>
        ) : (
          <span className="text-muted">--</span>
        );
      },
      width: "120px",
    },

    {
      name: "Status",
      cell: (row) => renderStatusBadge(row.creditNoteStatus),
      width: "140px",
    },
    {
      name: "CN By / Date",
      cell: (row) =>
        row.creditNoteBy ? (
          <div>
            <p className="m-0 fw-medium">{row.creditNoteBy}</p>
            <p className="m-0 text-muted">
              {row.creditNoteDate
                ? new Date(row.creditNoteDate).toLocaleDateString("en-IN")
                : ""}
            </p>
          </div>
        ) : (
          <span className="text-muted">--</span>
        ),
      width: "140px",
    },
    {
      name: "Stock In By / Date",
      cell: (row) =>
        row.stockInBy ? (
          <div>
            <p className="m-0 fw-medium">{row.stockInBy}</p>
            <p className="m-0 text-muted">
              {row.stockInDate
                ? new Date(row.stockInDate).toLocaleDateString("en-IN")
                : ""}
            </p>
          </div>
        ) : (
          <span className="text-muted">--</span>
        ),
      width: "150px",
    },
    // {
    //   name: "Action",
    //   cell: (row) => {
    //     const status = row.creditNoteStatus;

    //     // productReturned: true = physically stocked in, permanently locked
    //     if (status === "StockIn" || row.productReturned) {
    //       return (
    //         <span className="text-success fw-bold" style={{ fontSize: "12px" }}>
    //           ✓ Completed
    //         </span>
    //       );
    //     }

    //     if (status === "CreditNote") {
    //       return isStoreUser ? (
    //         <button
    //           className="btn btn-sm btn-success"
    //           style={{ fontSize: "11px", padding: "4px 10px" }}
    //           onClick={() => openConfirm("stockIn", row)}
    //           disabled={loading}
    //         >
    //           Stock In
    //         </button>
    //       ) : (
    //         <span className="text-warning fw-bold" style={{ fontSize: "11px" }}>
    //           Awaiting Stock In
    //         </span>
    //       );
    //     }

    //     // No action yet — PCD/Sales Admin can mark credit note
    //     // productReturned guard: if already returned via another flow, lock this too
    //     // if (!row.productReturned && isSalesAdminOrPCD) {
    //     //   return (
    //     //     <button
    //     //       className="btn btn-sm btn-warning"
    //     //       style={{ fontSize: "11px", padding: "4px 10px" }}
    //     //       onClick={() => openConfirm("creditNote", row)}
    //     //       disabled={loading}
    //     //     >
    //     //       Credit Note
    //     //     </button>
    //     //   );
    //     // }

    //     // After
    //     if (!row.productReturned && isSalesAdminOrPCD) {
    //       return (
    //         <button
    //           className="btn btn-sm btn-warning"
    //           style={{ fontSize: "11px", padding: "4px 10px" }}
    //           onClick={() => openConfirm("creditNote", row)}
    //           disabled={loading || isRecordExtended}
    //           title={
    //             isRecordExtended ? "ERF is extended — credit note locked" : ""
    //           }
    //         >
    //           Credit Note
    //         </button>
    //       );
    //     }

    //     return (
    //       <span className="text-muted" style={{ fontSize: "12px" }}>
    //         --
    //       </span>
    //     );
    //   },

    //   width: "180px",
    // },
    {
      name: "Action",
      cell: (row) => {
        const status = row.creditNoteStatus;

        // productReturned: true = physically stocked in, permanently locked
        if (status === "StockIn" || row.productReturned) {
          return (
            <span className="text-success fw-bold" style={{ fontSize: "12px" }}>
              ✓ Completed
            </span>
          );
        }

        // Item is marked CreditNote — show Stock In (Store) + Reverse (PCD) buttons.
        if (status === "CreditNote") {
          const reverseTitle = isInvoiceCreated
            ? "Invoice already generated — cannot reverse"
            : isFinanceConfirmed
              ? "Finance collection confirmed — cannot reverse"
              : isRecordExtended
                ? "ERF is extended — reverse locked"
                : "Undo credit note mark";

          return (
            // <div className="d-flex gap-2 align-items-center">
            // <div className="d-flex align-items-center gap-2 flex-nowrap">
            //   {isStoreUser ? (
            //     <button
            //       className="btn btn-sm btn-success"
            //       style={{ fontSize: "11px", padding: "4px 10px" }}
            //       onClick={() => openConfirm("stockIn", row)}
            //       disabled={loading}
            //     >
            //       Stock In
            //     </button>
            //   ) : (
            //     <span
            //       className="text-warning fw-bold"
            //       style={{ fontSize: "11px" }}
            //     >
            //       Awaiting Stock In
            //     </span>
            //   )}

            //   {isSalesAdminOrPCD && (
            //     <button
            //       className="btn btn-sm btn-outline-danger"
            //       style={{ fontSize: "11px", padding: "4px 10px" }}
            //       onClick={() => openConfirm("reverseCreditNote", row)}
            //       disabled={loading || isLockedForCreditNote}
            //       title={reverseTitle}
            //     >
            //       Reverse
            //     </button>
            //   )}
            // </div>
            <div className="d-flex align-items-center gap-2 flex-nowrap">
              {isStoreUser ? (
                <button
                  className="btn btn-sm btn-success"
                  onClick={() => openConfirm("stockIn", row)}
                >
                  <i className="fas fa-box-open me-1"></i>
                  Stock In
                </button>
              ) : (
                <span className="badge bg-warning text-dark">
                  Awaiting Stock In
                </span>
              )}

              {isSalesAdminOrPCD && (
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => openConfirm("reverseCreditNote", row)}
                  disabled={loading || isLockedForCreditNote}
                  title={reverseTitle}
                  style={{ whiteSpace: "nowrap" }}
                >
                  <i className="fas fa-undo me-1"></i>
                  Reverse
                </button>
              )}
            </div>
          );
        }

        // No status — PCD/Sales Admin can mark credit note (unless locked)
        if (!row.productReturned && isSalesAdminOrPCD) {
          const markTitle = isInvoiceCreated
            ? "Invoice already generated — cannot mark"
            : isFinanceConfirmed
              ? "Finance collection confirmed — cannot mark"
              : isRecordExtended
                ? "ERF is extended — credit note locked"
                : "";

          return (
            <button
              className="btn btn-sm btn-warning"
              style={{ fontSize: "11px", padding: "4px 10px" }}
              onClick={() => openConfirm("creditNote", row)}
              disabled={loading || isLockedForCreditNote}
              title={markTitle}
            >
              Credit Note
            </button>
          );
        }

        return (
          <span className="text-muted" style={{ fontSize: "12px" }}>
            --
          </span>
        );
      },
      width: "220px",
    },
  ];
  if (isExtension) {
    return null;
  }

  if (!erfRecord || allItems.length === 0) {
    return null;
  }
  // if (erfRecord.erfStatusCode !== "DPD"){ return null;}

  return (
    <>
      <div className="accordion mt-4" id="accordionCreditNote">
        <div className="accordion-item">
          <h2 className="accordion-header">
            <Row className="accordion-button">
              <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                <i
                  className={`bi ${
                    isSectionVisible ? "bi-chevron-right" : "bi-chevron-down"
                  }`}
                  onClick={() => setIsSectionVisible(!isSectionVisible)}
                ></i>
              </Col>
              <Col xs={1} md={2} lg={2} className="text-center logoBtn">
                <span className="iconLogo">CN</span>
              </Col>
              <Col xs={12} md={6} lg={6}>
                <span className="headerTitle">
                  Products Credit Note & Stock Return
                </span>
                <span className="subHeaderTitle">
                  Mark returned items for credit note and stock-in
                </span>
              </Col>
              <Col xs={12} md={3} lg={3} className="headerButtonAlign">
                <span
                  className="badge bg-success"
                  style={{ fontSize: "13px", padding: "6px 12px" }}
                >
                  Products Credit Note: {totalCreditNoteAmount.toFixed(2)} INR
                </span>
              </Col>
            </Row>
          </h2>

          <div
            className={`accordion-collapse collapse show ${
              isSectionVisible ? "visible" : ""
            }`}
          >
            <div className="accordion-body p-0">
              {loading ? (
                <div className="p-3 text-center text-muted">Loading...</div>
              ) : (
                <>
                  <DataTable
                    columns={columns}
                    data={allItems}
                    pagination
                    noDataComponent={
                      <div className="p-3 text-center text-muted">
                        No line items found
                      </div>
                    }
                  />
                  {/* <div
                      className="d-flex justify-content-end p-3"
                      style={{ borderTop: "1px solid #dee2e6" }}
                    >
                      <div className="text-end">
                        <p
                          className="m-0 text-muted"
                        >
                          Total Credit Note Amount
                        </p>
                        <p
                          className="m-0 fw-bold text-danger"
                        >
                          ₹ {totalCreditNoteAmount.toFixed(2)}
                        </p>
                        <p
                          className="m-0 text-muted"
                        >
                          {
                            allItems.filter(
                              (i) =>
                                i.creditNoteStatus === "CreditNote" ||
                                i.creditNoteStatus === "StockIn",
                            ).length
                          }{" "}
                          item(s) credited
                        </p>
                      </div>
                    </div> */}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {/* Confirmation Modal */}
      <Modal
        show={confirmModal.show}
        onHide={() =>
          setConfirmModal({
            show: false,
            action: null,
            row: null,
          })
        }
        centered
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {confirmModal.action === "creditNote" && "Confirm Credit Note"}

            {confirmModal.action === "stockIn" && "Confirm Stock In"}

            {confirmModal.action === "reverseCreditNote" &&
              "Confirm Reverse Credit Note"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* CREDIT NOTE */}
          {confirmModal.action === "creditNote" && (
            <>
              <div className="border rounded p-3 mb-3 bg-warning bg-opacity-10">
                <div className="fw-bold text-warning mb-1">
                  Credit Note Confirmation
                </div>

                <small className="text-muted">
                  This item will be marked as Credit Note.
                </small>
              </div>

              <div className="row mb-2">
                <div className="col-4 fw-semibold">Product</div>

                <div className="col-8">
                  {confirmModal.row?.materialDescription}
                </div>
              </div>

              <div className="row mb-2">
                <div className="col-4 fw-semibold">Material Code</div>

                <div className="col-8">{confirmModal.row?.materialCode}</div>
              </div>

              <div className="row mb-2">
                <div className="col-4 fw-semibold">Barcode</div>

                <div className="col-8">{confirmModal.row?.barcode || "--"}</div>
              </div>

              <div className="row">
                <div className="col-4 fw-semibold">Credit Note Amount</div>

                <div className="col-8 text-danger fw-bold">
                  ₹ {parseFloat(confirmModal.row?.grossValue || 0).toFixed(2)}
                </div>
              </div>
            </>
          )}

          {/* STOCK IN */}
          {confirmModal.action === "stockIn" && (
            <>
              <div className="border rounded p-3 mb-3 bg-success bg-opacity-10">
                <div className="fw-bold text-success mb-1">
                  Stock In Confirmation
                </div>

                <small className="text-muted">
                  Item will be marked as returned to stock.
                </small>
              </div>

              <div className="row mb-2">
                <div className="col-4 fw-semibold">Product</div>

                <div className="col-8">
                  {confirmModal.row?.materialDescription}
                </div>
              </div>

              <div className="row mb-2">
                <div className="col-4 fw-semibold">Material Code</div>

                <div className="col-8">{confirmModal.row?.materialCode}</div>
              </div>

              <div className="row">
                <div className="col-4 fw-semibold">Barcode</div>

                <div className="col-8">{confirmModal.row?.barcode || "--"}</div>
              </div>
            </>
          )}

          {/* REVERSE CREDIT NOTE */}
          {confirmModal.action === "reverseCreditNote" && (
            <>
              <div className="border rounded p-3 mb-3 bg-danger bg-opacity-10">
                <div className="fw-bold text-danger mb-1">
                  Reverse Credit Note
                </div>

                <small className="text-muted">
                  This action will remove the credit note and restore the item.
                </small>
              </div>

              <div className="row mb-2">
                <div className="col-4 fw-semibold">Product</div>

                <div className="col-8">
                  {confirmModal.row?.materialDescription}
                </div>
              </div>

              <div className="row mb-2">
                <div className="col-4 fw-semibold">Material Code</div>

                <div className="col-8">{confirmModal.row?.materialCode}</div>
              </div>

              <div className="row mb-2">
                <div className="col-4 fw-semibold">Barcode</div>

                <div className="col-8">{confirmModal.row?.barcode || "--"}</div>
              </div>

              <div className="row">
                <div className="col-4 fw-semibold">Credit Note Amount</div>

                <div className="col-8 text-danger fw-bold">
                  ₹{" "}
                  {parseFloat(
                    confirmModal.row?.creditNoteAmount ||
                      confirmModal.row?.grossValue ||
                      0,
                  ).toFixed(2)}
                </div>
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="pt-2">
          <button
            className="btnTable btn-danger px-4"
            onClick={() =>
              setConfirmModal({
                show: false,
                action: null,
                row: null,
              })
            }
            disabled={loading}
          >
            Cancel
          </button>

          <button
            className="btnTable px-4"
            onClick={executeAction}
            disabled={loading}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                />
                Processing...
              </>
            ) : (
              <>
                {confirmModal.action === "creditNote" && "Confirm Credit Note"}

                {confirmModal.action === "stockIn" && "Confirm Stock In"}

                {confirmModal.action === "reverseCreditNote" &&
                  "Reverse Credit Note"}
              </>
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ERFCreditNoteSection;
